import React, { type FC, type ReactNode, useEffect, useState } from 'react';

import CodeMirror, { type Extension } from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { html } from '@codemirror/lang-html';

import { CopyIcon } from '@shared/ui/icons';

import { copyToClipboard } from '@utils/copyToClipboard';

type Language = 'json' | 'html';

type CodeEditorProps = {
  language: Language;
  value: string | object;
  title: string | ReactNode;
  classNameWrapper?: string;
  onChange: (value: string | object) => void;
  height?: string;
  required?: boolean;
  theme?: 'light' | 'dark';
};

const languageModules = {
  json,
  html,
};

export const CodeEditor: FC<CodeEditorProps> = ({
  language,
  value,
  onChange,
  classNameWrapper,
  title,
  height = '200px',
  theme = 'light',
  required = false,
}) => {
  const [extension, setExtension] = useState<Extension | null>(null);

  useEffect(() => {
    const loadExtension = () => {
      const extensionFunc = languageModules[language];
      if (extensionFunc) {
        setExtension(extensionFunc());
      } else {
        setExtension(null);
      }
    };

    loadExtension();
  }, [language]);

  const stringValue = (() => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    // For JSON language, normalize string values (e.g., from previously
    // corrupted double-stringified saves) into clean formatted JSON.
    if (language === 'json') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  })();

  const hasContent = !!stringValue?.trim();

  const handleCopy = () => copyToClipboard(stringValue);

  return (
    <div className={classNameWrapper}>
      <div className={'rounded-3xl overflow-hidden border ant-input'}>
        <div
          className={
            'flex items-center justify-between rounded-t-sm bg-blueTone pl-4 pr-2 py-2'
          }
        >
          <h2 className={'text-1424 font-semibold text-black'}>
            {title}
            <span className='font-medium italic'>{` (${language})`}</span>
            {required && <span className='text-redOrange'>*</span>}
          </h2>
          <button
            type='button'
            onClick={handleCopy}
            disabled={!hasContent}
            className='flex items-center gap-0.5 rounded-sm px-1 text-1424 font-medium leading-[2.4rem] text-dodgerBlue text-right font-sans transition-colors hover:bg-waterLightHover50 hover:text-honoluluBlue focus-visible:bg-waterLightHover50 focus-visible:text-honoluluBlue focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-dodgerBlue'
            aria-label={`Copy ${
              typeof title === 'string' ? title : 'code editor'
            } content`}
            data-testid='code-editor-copy'
          >
            <CopyIcon
              className='h-2.5 w-2.5 [&_path]:fill-current'
              aria-hidden='true'
            />
            <span>Copy</span>
          </button>
        </div>
        {extension && (
          <CodeMirror
            value={stringValue}
            extensions={[extension]}
            theme={theme}
            height={height}
            onChange={val => {
              try {
                onChange(language === 'json' ? JSON.parse(val) : val);
              } catch {
                onChange(val);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};
